import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  getUserTimezone,
  setUserTimezone as saveTimezone,
  getSupportedTimezones
} from '../util/timezone.service';

type TimezoneContextValue = {
  timezone: string;
  setTimezone: (tz: string) => void;
  supportedTimezones: string[];
};

const TimezoneContext = createContext<TimezoneContextValue | undefined>(undefined);

type TimezoneProviderProps = {
  children: React.ReactNode;
};

export function TimezoneProvider({ children }: TimezoneProviderProps) {
  const [timezone, setTimezoneState] = useState<string>('UTC');

  // Initialize timezone on mount
  useEffect(() => {
    const currentTz = getUserTimezone();
    setTimezoneState(currentTz);
  }, []);

  const handleSetTimezone = (newTz: string) => {
    saveTimezone(newTz);
    setTimezoneState(newTz);
  };

  const value: TimezoneContextValue = useMemo(
    () => ({
      timezone,
      setTimezone: handleSetTimezone,
      supportedTimezones: getSupportedTimezones(),
    }),
    [timezone]
  );

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezoneContext(): TimezoneContextValue {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezoneContext must be used within TimezoneProvider');
  }
  return context;
}
